import dayjs from 'dayjs'
import { l } from '@manychat/manyui'

import { FieldType } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import {
  FilterCondition,
  FilterValueItem,
  FilterFieldOperatorData,
  Segment,
} from 'common/filter/interfaces'
import { formatDateTime } from 'common/usesList/utils'
import { formatUnit } from 'components/DelayPicker/utils'
import { Countries } from 'constants/CountryCodes'

import { FilterField, FilterChannelTypeLabel, FieldTypeOperator } from './constants'
import { isSystemCurrentDateTimeValue } from './helpers'

const fieldsWhitelist: Set<FilterField | string> = new Set([
  FilterField.TAG,
  FilterField.SEQUENCE,
  FilterField.WIDGET,
  FilterField.OPTIN_THROUGH_ADS,
  FilterField.ONE_TIME_NOTIFICATION,
  FilterField.ONE_TIME_NOTIFICATION_OPTIN,
  FilterField.IG_RECURRING_NOTIFICATION,
  FilterField.IG_RECURRING_NOTIFICATION_OPTIN,
  FilterField.SMART_SEGMENT,
])
const fieldFormulaValueMask = /.*\({{.*}}.*/

const formatWeekday = (day: string): string => {
  const firstLetter = day.charAt(0).toUpperCase()
  const nextTwoLetters = day.substring(1, 3)
  return firstLetter + nextTwoLetters
}

const joinWeekdays = (weekdays: string[]): string => {
  return weekdays.map(formatWeekday).reduce((result, day, index) => {
    if (result === '') return day
    return index === weekdays.length - 1 ? `${result} or ${day}` : `${result}, ${day}`
  }, '')
}

export function formatValue(
  value: FilterCondition['value'] | number[],
  fieldType: string,
  fieldName: string,
  segments?: FilterValueItem[] | Record<string, Segment>,
  operator?: FilterFieldOperatorData,
  options?: { botTimezone: string | null; useChangedLabelForOperators?: boolean },
) {
  if (!value && fieldType !== FieldType.NUMBER) {
    return ''
  }

  if (
    isSystemCurrentDateTimeValue(value) &&
    fieldType === FieldType.SYSTEM_CURRENT_DATETIME &&
    operator?.value === FieldTypeOperator.BETWEEN
  ) {
    const { from, to, time_zone: timeZone, weekdays } = value
    const botTZ = options?.botTimezone ? dayjs(value).tz(options.botTimezone).format('Z') : null
    const tz = timeZone ? `${timeZone}:00` : botTZ
    const weekdaysString = weekdays.length === 7 ? 'Any Day' : joinWeekdays(weekdays)
    return `${from.time} - ${to.time} (UTC ${tz}) Days: ${weekdaysString}`
  }

  if (
    typeof value === 'number' &&
    (operator?.value === FieldTypeOperator.DATETIME_INTERVAL_AFTER ||
      operator?.value === FieldTypeOperator.DATETIME_INTERVAL_BEFORE)
  ) {
    return `${formatUnit(Math.abs(value))} ago`
  }

  if (
    typeof value === 'string' &&
    fieldFormulaValueMask.test(value) &&
    (fieldType.indexOf('date') >= 0 || fieldType === FieldType.SYSTEM_CURRENT_DATETIME)
  ) {
    return 'Custom Field date'
  }

  if ((typeof value === 'string' || typeof value === 'number') && fieldType === FieldType.DATE) {
    return l.date(value)
  }

  if (
    (typeof value === 'string' || Array.isArray(value)) &&
    (fieldType === FieldType.DATETIME ||
      fieldType === FieldType.SYSTEM_DATETIME ||
      fieldType === FieldType.SYSTEM_CURRENT_DATETIME)
  ) {
    if (options?.botTimezone) {
      const dateOnly = value?.length < 11
      return formatDateTime(value, dateOnly, options.botTimezone)
    }

    return formatDateTime(value)
  }

  if (fieldsWhitelist.has(fieldName) && !Array.isArray(segments)) {
    const segment = segments?.[`${fieldName}_${value}`]

    if (!segment) {
      return ''
    }

    return segment.label
  }

  if (typeof value === 'string' && fieldName === FilterField.PHONE_COUNTRY_CODE) {
    const countryName = Countries[value]?.name
    return `${value}${countryName ? ` (${countryName})` : ''}`
  }

  if (typeof value === 'string' && fieldName === FilterField.CONVERSATION_CHANNEL) {
    return FilterChannelTypeLabel[value] || value
  }

  return value
}

function formatFilterCondition(
  value: FilterCondition['value'],
  fieldDesc: Field,
  operatorDesc: FilterFieldOperatorData,
  segments?: FilterValueItem[] | Record<string, Segment>,
  options?: { botTimezone: string | null; useChangedLabelForOperators?: boolean },
): [string, string, string] {
  const formattedValue = formatValue(
    value,
    fieldDesc.type,
    fieldDesc.id,
    segments,
    operatorDesc,
    options,
  )

  const label = options?.useChangedLabelForOperators
    ? operatorDesc.changedLabel || ''
    : operatorDesc.label

  return [fieldDesc.caption, label, `${formattedValue}`]
}

export default formatFilterCondition
